
/**
 * Audio utility functions for handling audio data
 */

/**
 * Creates a URL from a blob with explicit MIME type
 */
export function createAudioUrl(blob: Blob, mimeType?: string): string {
  // Ensure proper MIME type for better iOS compatibility
  const safeBlob = mimeType 
    ? new Blob([blob], { type: mimeType }) 
    : blob;
    
  return URL.createObjectURL(safeBlob);
}

/**
 * Safely revokes an object URL
 */
export function revokeAudioUrl(url: string | null): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Detects if the current device is running iOS
 */
export function isIOSDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor;
  // Safely check for MSStream property existence using type assertion
  const windowWithMSStream = window as Window & { MSStream?: any };
  return /iPad|iPhone|iPod/.test(userAgent) && !windowWithMSStream.MSStream;
}

/**
 * Detects if the current browser is Safari
 */
export function isSafariBrowser(): boolean {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Safari') && !userAgent.includes('Chrome');
}

/**
 * Helper to create an audio element with iOS-optimized settings
 */
export function createOptimizedAudioElement(): HTMLAudioElement {
  const audio = new Audio();
  
  // iOS optimizations
  if (isIOSDevice() || isSafariBrowser()) {
    audio.preload = "auto";
  }
  
  return audio;
}

/**
 * Convert a base64 string to a Blob with proper MIME type
 */
export function base64ToAudioBlob(base64Data: string, mimeType: string = 'audio/mpeg'): Blob {
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  return new Blob([bytes], { type: mimeType });
}
